import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

/**
 * Small SVG charts for the dashboard, drawn with the design system's theme tokens (no chart
 * library). Every chart has a text alternative and works in light and dark themes.
 */

// ---------------------------------------------------------------- maths

/** Round axis steps (1, 2, 2.5, 5 × 10ⁿ) covering 0..max with about `count` intervals. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const ticks: number[] = [];
  for (let v = 0; v < max + step * 0.999; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return ticks.length > 1 ? ticks : [0, step];
}

/** Smooth path through the points without overshooting (monotone cubic, Fritsch–Carlson). */
function smoothPath(pts: Array<[number, number]>): string {
  if (pts.length === 0) return '';
  if (pts.length < 3) return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  const n = pts.length;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1]![0] - pts[i]![0]);
    slope.push((pts[i + 1]![1] - pts[i]![1]) / dx[i]!);
  }
  const t: number[] = [slope[0]!];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1]!;
    const b = slope[i]!;
    t.push(
      a * b <= 0
        ? 0
        : (3 * (dx[i - 1]! + dx[i]!)) / ((2 * dx[i]! + dx[i - 1]!) / a + (dx[i]! + 2 * dx[i - 1]!) / b),
    );
  }
  t.push(slope[n - 2]!);
  let d = `M${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const h = dx[i]! / 3;
    d += ` C${x0 + h},${y0 + t[i]! * h} ${x1 - h},${y1 - t[i + 1]! * h} ${x1},${y1}`;
  }
  return d;
}

function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ---------------------------------------------------------------- sparkline

/** Tiny trend line with a soft fill and an end dot (design system fn-spark). */
export function Sparkline({
  values,
  color = 'var(--chart-1)',
  height = 40,
  label,
}: {
  values: number[];
  color?: string;
  height?: number;
  label: string;
}) {
  const id = useId();
  const W = 200;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const pts: Array<[number, number]> = values.map((v, i) => [
    values.length > 1 ? (i / (values.length - 1)) * W : W,
    height - 3 - ((v - min) / span) * (height - 8),
  ]);
  const line = smoothPath(pts);
  const last = pts[pts.length - 1];
  return (
    <svg
      className="fn-chart-spark"
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {pts.length > 1 && (
        <>
          <path d={`${line} L${W},${height} L0,${height} Z`} fill={`url(#${id})`} />
          <path
            d={line}
            fill="none"
            stroke={color}
            strokeWidth="1.75"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        </>
      )}
      {last && max > 0 && <circle cx={last[0] - 2} cy={last[1]} r="2.5" fill={color} />}
    </svg>
  );
}

// ---------------------------------------------------------------- area chart

export interface AreaPoint {
  /** Axis label (e.g. "Okt"). */
  label: string;
  /** Tooltip heading (e.g. "Oktober 2026"). */
  title: string;
  value: number;
  /** Added in this period, shown in the tooltip. */
  added: number;
}

/** Cumulative series as a smooth area with grid, axis, end marker and a hover tooltip. */
export function AreaChart({
  points,
  color,
  format,
  axisFormat,
  height = 240,
  label,
}: {
  points: AreaPoint[];
  color: string;
  format: (v: number) => string;
  axisFormat: (v: number) => string;
  height?: number;
  label: string;
}) {
  const id = useId();
  const [box, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { top: 14, right: 16, bottom: 26, left: 52 };
  const w = Math.max(0, width - pad.left - pad.right);
  const h = height - pad.top - pad.bottom;
  const max = Math.max(0, ...points.map((p) => p.value));
  const ticks = niceTicks(max, 4);
  const top = ticks[ticks.length - 1]!;
  const x = (i: number) => pad.left + (points.length > 1 ? (i / (points.length - 1)) * w : w / 2);
  const y = (v: number) => pad.top + h - (v / top) * h;
  const pts = points.map((p, i) => [x(i), y(p.value)] as [number, number]);
  const line = smoothPath(pts);
  const empty = max === 0;
  const hp = hover === null ? null : points[hover];

  return (
    <div ref={box} className="fn-chart" style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`${label}: ${points.map((p) => `${p.title} ${format(p.value)}`).join(', ')}`}
          onPointerMove={(e) => {
            if (points.length === 0) return;
            const r = e.currentTarget.getBoundingClientRect();
            const rel = (e.clientX - r.left - pad.left) / (w || 1);
            setHover(Math.max(0, Math.min(points.length - 1, Math.round(rel * (points.length - 1)))));
          }}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.32" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={pad.left}
                x2={pad.left + w}
                y1={y(t)}
                y2={y(t)}
                className="fn-chart__grid"
                strokeDasharray={t === 0 ? undefined : '3 4'}
              />
              <text x={pad.left - 10} y={y(t) + 4} textAnchor="end" className="fn-chart__axis">
                {axisFormat(t)}
              </text>
            </g>
          ))}
          {points.map((p, i) => (
            <text key={p.title} x={x(i)} y={height - 6} textAnchor="middle" className="fn-chart__axis">
              {p.label}
            </text>
          ))}
          {!empty && pts.length > 1 && (
            <g className="fn-chart__series">
              <path
                d={`${line} L${pts[pts.length - 1]![0]},${pad.top + h} L${pts[0]![0]},${pad.top + h} Z`}
                fill={`url(#${id})`}
              />
              <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
            </g>
          )}
          {!empty && pts.length > 0 && hover === null && (
            <circle
              cx={pts[pts.length - 1]![0]}
              cy={pts[pts.length - 1]![1]}
              r="5"
              fill={color}
              className="fn-chart__dot"
            />
          )}
          {hp && hover !== null && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + h} className="fn-chart__cross" />
              <circle cx={x(hover)} cy={y(hp.value)} r="5" fill={color} className="fn-chart__dot" />
            </g>
          )}
          <rect x={pad.left} y={pad.top} width={w} height={h} fill="transparent" />
        </svg>
      )}
      {empty && width > 0 && <div className="fn-chart__empty">Belum ada data pada periode ini</div>}
      {hp && hover !== null && (
        <div
          className="fn-chart__tip"
          style={{
            left: Math.min(Math.max(x(hover), 90), width - 90),
            top: Math.max(4, y(hp.value) - 70),
          }}
        >
          <div className="fn-chart__tip-title">{hp.title}</div>
          <div className="fn-chart__tip-value">
            <span className="fn-swatch" style={{ background: color, width: 10, height: 10 }} />
            {format(hp.value)}
          </div>
          <div className="fn-chart__tip-meta">
            {hp.added > 0 ? `+${format(hp.added)} bulan ini` : 'Tidak ada penambahan'}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- donut

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Value as shown in the legend; defaults to the number. */
  display?: string;
}

/** Arc lengths along a circle of circumference `c`, leaving `gap` between non-empty segments. */
export function donutArcs(
  values: number[],
  c: number,
  gap: number,
): Array<{ offset: number; length: number }> {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  const shown = values.filter((v) => v > 0).length;
  const g = shown > 1 ? gap : 0;
  let at = 0;
  return values.map((v) => {
    const full = total > 0 ? (Math.max(0, v) / total) * c : 0;
    const arc = { offset: at + (full > 0 ? g / 2 : 0), length: Math.max(0, full - g) };
    at += full;
    return arc;
  });
}

/** Donut with the total in the middle and a legend with values and shares. */
export function DonutChart({
  segments,
  center,
  centerLabel,
  label,
  size = 188,
}: {
  segments: DonutSegment[];
  center: ReactNode;
  centerLabel: string;
  label: string;
  size?: number;
}) {
  const [active, setActive] = useState<string | null>(null);
  const stroke = 22;
  const r = (size - stroke - 8) / 2;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0);
  const arcs = useMemo(
    () =>
      donutArcs(
        segments.map((s) => s.value),
        c,
        3,
      ),
    [segments, c],
  );
  const pct = (v: number) => (total > 0 ? Math.round((v / total) * 1000) / 10 : 0);

  return (
    <div className="fn-donut">
      <div className="fn-donut__ring" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={`${label}: ${segments.map((s) => `${s.label} ${s.display ?? s.value}`).join(', ')}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--fill-muted)"
            strokeWidth={stroke}
          />
          {total > 0 &&
            segments.map((s, i) =>
              s.value > 0 ? (
                <circle
                  key={s.key}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={active === s.key ? stroke + 6 : stroke}
                  strokeDasharray={`${arcs[i]!.length} ${c - arcs[i]!.length}`}
                  strokeDashoffset={-arcs[i]!.offset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                  className="fn-donut__seg"
                  opacity={active && active !== s.key ? 0.45 : 1}
                  onPointerEnter={() => setActive(s.key)}
                  onPointerLeave={() => setActive(null)}
                >
                  <title>{`${s.label}: ${s.display ?? s.value} (${pct(s.value)}%)`}</title>
                </circle>
              ) : null,
            )}
        </svg>
        <div className="fn-donut__center">
          <span className="fn-donut__value">{total > 0 ? center : '—'}</span>
          <span className="fn-donut__label">{total > 0 ? centerLabel : 'Belum ada data'}</span>
        </div>
      </div>
      <ul className="fn-donut__legend">
        {segments.map((s) => (
          <li
            key={s.key}
            className={active === s.key ? 'is-active' : undefined}
            onPointerEnter={() => setActive(s.key)}
            onPointerLeave={() => setActive(null)}
          >
            <span className="fn-swatch" style={{ background: s.color, width: 10, height: 10 }} />
            <span className="fn-donut__name">{s.label}</span>
            <span className="fn-donut__num">{s.display ?? s.value}</span>
            <span className="fn-donut__pct">{nfPct(pct(s.value))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const nfPct = (v: number) => `${v.toLocaleString('id-ID', { maximumFractionDigits: 1 })}%`;
